<template>
  <div class="section-card">
    <h3><i class="bi bi-file-earmark-text"></i> Contrato digital</h3>

    <div v-if="loading" class="text-center p-2">
      <div class="spinner-border spinner-border-sm" role="status"></div>
    </div>

    <template v-else>
      <div v-if="assinado" class="alert alert-success py-2 mb-2">
        <i class="bi bi-patch-check-fill me-1"></i>
        Assinado por <strong>{{ assinado.signer_name }}</strong> ({{ assinado.signer_document }})
        em {{ dataHora(assinado.signed_at) }}.
        <a :href="link(assinado)" target="_blank" rel="noopener" class="ms-1">Ver contrato</a>
      </div>

      <div v-if="pendente" class="mb-2">
        <p class="small text-muted mb-1">Aguardando assinatura do cliente. Envie o link:</p>
        <div class="input-group input-group-sm mb-2">
          <input class="form-control" :value="link(pendente)" readonly aria-label="Link do contrato">
          <button class="btn btn-outline-secondary" type="button" @click="copiar(pendente)"><i class="bi bi-clipboard"></i></button>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <a :href="whatsapp(pendente)" target="_blank" rel="noopener" class="btn btn-sm btn-success">
            <i class="bi bi-whatsapp me-1"></i>Enviar pelo WhatsApp
          </a>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="cancelar(pendente)" :disabled="acao">Cancelar</button>
        </div>
      </div>

      <p v-if="!assinado && !pendente" class="small text-muted mb-2">Nenhum contrato gerado para esta locação.</p>

      <button type="button" class="btn btn-sm btn-outline-primary" @click="gerar" :disabled="acao">
        <span v-if="acao" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        {{ pendente || assinado ? 'Gerar novo contrato' : 'Gerar contrato' }}
      </button>
      <p v-if="pendente || assinado" class="small text-muted mt-1 mb-0">
        Gere um novo se a locação mudou: o contrato guarda os dados do momento em que foi gerado.
      </p>
    </template>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { contracts } from '@/models/contracts';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';

export default {
  name: 'ContractCard',
  props: {
    rentalId:    { type: [Number, String], required: true },
    clientPhone: { type: String, default: '' },
  },
  setup(props) {
    const toastStore = useToastStore();
    const model = contracts();
    const lista = ref([]);
    const loading = ref(false);
    const acao = ref(false);

    const pendente = computed(() => lista.value.find(c => c.status === 'PENDING'));
    const assinado = computed(() => lista.value.find(c => c.status === 'SIGNED'));

    async function carregar() {
      loading.value = true;
      try {
        lista.value = await model.listByRental(props.rentalId);
      } catch (error) {
        toastStore.error('Erro ao carregar contratos: ' + parseError(error));
      }
      loading.value = false;
    }

    async function gerar() {
      if ((pendente.value || assinado.value) && !confirm('Gerar um novo contrato? O link pendente atual deixa de valer.')) return;
      acao.value = true;
      try {
        await model.generate(props.rentalId);
        toastStore.success('Contrato gerado.');
        await carregar();
      } catch (error) {
        toastStore.error('Erro ao gerar contrato: ' + parseError(error));
      }
      acao.value = false;
    }

    async function cancelar(c) {
      if (!confirm('Cancelar este contrato? O link deixa de funcionar.')) return;
      acao.value = true;
      try {
        await model.cancel(c.id);
        await carregar();
      } catch (error) {
        toastStore.error(parseError(error));
      }
      acao.value = false;
    }

    function link(c) {
      return `${window.location.origin}/c/${c.token}`;
    }

    function whatsapp(c) {
      let numero = String(props.clientPhone || '').replace(/\D/g, '');
      if (numero && numero.length <= 11) numero = '55' + numero;
      const texto = encodeURIComponent(`Olá! Segue o contrato da sua locação para leitura e assinatura: ${link(c)}`);
      return numero ? `https://wa.me/${numero}?text=${texto}` : `https://wa.me/?text=${texto}`;
    }

    async function copiar(c) {
      try {
        await navigator.clipboard.writeText(link(c));
        toastStore.success('Link copiado.');
      } catch (e) {
        toastStore.warning('Não foi possível copiar. Selecione o link e copie manualmente.');
      }
    }

    function dataHora(valor) {
      return valor ? new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';
    }

    onMounted(carregar);

    return { lista, loading, acao, pendente, assinado, gerar, cancelar, link, whatsapp, copiar, dataHora };
  }
}
</script>

<style scoped>
/* Mesmo visual dos títulos de seção da tela de detalhes da locação
   (o estilo de lá é scoped e não alcança o h3 deste componente) */
h3 {
  font-size: 0.95rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  color: var(--ink);
}

h3 i {
  color: var(--brand);
}
</style>
